import { Font } from "@react-pdf/renderer";

import "./fonts";

// react-pdf garbles the surrounding text when it meets a character the font
// has no glyph for (emoji, the invisible fillers used for blank lines in
// posts, "fancy" letters such as 𝐌𝐞𝐫𝐫𝐲, …), so text is cleaned before it
// reaches the A4 PDF.

// Emoji are removed outright, even where the font has a plain glyph for them.
// ★/☆ are not pictographic, so review stars are unaffected.
const EMOJI_REGEX =
  /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}‍︎️⃣\u{e0020}-\u{e007f}]/gu;

// Line breaks and tabs are laid out by react-pdf rather than drawn as glyphs
const LAYOUT_CHARACTERS = new Set(["\n", "\r", "\t"]);

type CanRender = (char: string) => boolean;

const sanitizeText = (text: string, canRender: CanRender) =>
  Array.from(text.replace(EMOJI_REGEX, ""))
    .map((char) => {
      if (LAYOUT_CHARACTERS.has(char) || canRender(char)) return char;
      // Stylised letters and digits (𝐌, 𝓜, 𝟏, …) have plain equivalents
      const normalized = char.normalize("NFKC");
      return normalized !== char && Array.from(normalized).every(canRender)
        ? normalized
        : "";
    })
    .join("")
    // Tidy the spaces left behind where a removed character sat between words
    .replace(/ {2,}/g, " ")
    .replace(/^ +| +$/gm, "");

const sanitizeDeep = <T>(value: T, sanitize: (text: string) => string): T => {
  if (typeof value === "string") {
    return sanitize(value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeDeep(item, sanitize)) as unknown as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        sanitizeDeep(entry, sanitize),
      ])
    ) as unknown as T;
  }
  return value;
};

/**
 * Loads the PDF font and returns a function that makes a copy of a value with
 * every string in it limited to characters the font can render.
 */
export const loadPdfTextSanitizer = async () => {
  const fontSource = Font.getFont({ fontFamily: "NotoSansJP", fontWeight: 400 });
  await fontSource.load();
  const font = fontSource.data;
  const canRender: CanRender = (char) =>
    // If the font somehow failed to load, only remove emoji
    !font || font.hasGlyphForCodePoint(char.codePointAt(0) as number);

  return <T>(value: T): T =>
    sanitizeDeep(value, (text) => sanitizeText(text, canRender));
};
